"use client"

import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import GalleryController from '@components/lists/offer-card/GalleryController'
import WatchController from '@components/lists/WatchController'
import { hasToken, register, setToken } from 'lib/watch/watchStore'
import { ListingOfferCard } from '@components/lists/offer-card/OfferCard'
import { isOfferFresh } from '@components/lists/isOfferFresh'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import pluralize from '@utils/grammar/pluralize'
import salonLink from '@utils/links/salonLink'
import mix from '@utils/styles/mix'
import readPriceSlug from '@utils/slug/readPriceSlug'
import numericSlug from '@utils/slug/numericSlug'

import cardStyles from '@components/lists/offer-card/OfferCard.module.css'
import offerStyles from 'src/pagesComponents/display/v2/Offer.module.css'
import styles from './Salon.module.css'

const HOST = process.env.NEXT_PUBLIC_HOST
const OFFERS = ['ogłoszenie', 'ogłoszenia', 'ogłoszeń']
// parametry oferty w adresie (dotychczasowe i nowe zakresy); inne parametry nie są przenoszone
const PARAMS = ['zakladka', 'strona', 'sortuj', 'marka', 'model', 'typ', 'paliwo', 'skrzynia-biegow', 'cena-od', 'cena-do', 'rocznik-od', 'rocznik-do']
const FILTERS = ['marka', 'model', 'cena-od', 'cena-do', 'rocznik-od', 'rocznik-do', 'typ', 'paliwo', 'skrzynia-biegow']
const LABELS = { marka: 'Marka', model: 'Model', 'cena-od': 'Cena od', 'cena-do': 'Cena do', 'rocznik-od': 'Rok od', 'rocznik-do': 'Rok do', typ: 'Typ', paliwo: 'Paliwo', 'skrzynia-biegow': 'Skrzynia biegów' }
const SORTS = [['najnowsze', 'Najnowsze'], ['popularne', 'Popularne'], ['najtansze', 'Najtańsze'], ['najdrozsze', 'Najdroższe']]
const PRICES = [5000, 10000, 20000, 30000, 40000, 50000, 60000, 80000, 100000, 150000, 200000, 300000, 500000]
const YEAR_NOW = new Date().getFullYear()
const YEARS = Array.from({ length: YEAR_NOW - 1999 }, (_, i) => YEAR_NOW - i).concat([1995, 1990, 1980])
const SIZES = '(min-width: 1280px) 376px, (min-width: 1024px) calc((100vw - 80px) / 2), (min-width: 640px) calc((100vw - 64px) / 2), calc(100vw - 32px)'

const first = (v) => (Array.isArray(v) ? v[0] : v)
const formatZl = (n) => `${String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} zł`
function paramsOf (query) {
    const out = {}
    for (const k of PARAMS) { const v = first(query[k]); if (typeof v === 'string' && v !== '') out[k] = v }
    return out
}
const chipValue = (k, v) => (/^cena/.test(k) ? formatZl(readPriceSlug(v)) : v)

// Zmiana parametrów oferty: ten sam adres wizytówki (subdomena albo /salon/<nazwa>) z nowymi parametrami, bez przewijania;
// dane strony z serwera (getServerSideProps).
function useOfferNavigation (subdomain) {
    const router = useRouter()
    const [busy, setBusy] = useState(false)
    useEffect(() => {
        const on = () => setBusy(true), off = () => setBusy(false)
        router.events.on('routeChangeStart', on)
        router.events.on('routeChangeComplete', off)
        router.events.on('routeChangeError', off)
        return () => { router.events.off('routeChangeStart', on); router.events.off('routeChangeComplete', off); router.events.off('routeChangeError', off) }
    }, [router])
    const params = paramsOf(router.query)
    const href = (changes) => {
        const q = { ...params, ...changes }
        for (const k of Object.keys(q)) if (!q[k] || (k === 'strona' && String(q[k]) === '1')) delete q[k]
        const qs = new URLSearchParams(q).toString()
        return { q, qs, url: `${salonLink(subdomain)}/${qs ? `?${qs}` : ''}` }
    }
    const go = (changes) => {
        const { q, qs } = href(changes)
        const path = window.location.pathname
        return router.push({ pathname: router.pathname, query: { ...q, subdomain: router.query.subdomain } }, `${path}${qs ? `?${qs}` : ''}`, { scroll: false })
    }
    return { busy, go, href, params }
}

function Select ({ id, label, onChange, options, placeholder, value, disabled }) {
    return (
        <div className={styles.field}>
            <label htmlFor={id}>{label}</label>
            <select className={styles.control} disabled={disabled} id={id} onChange={e => onChange(e.target.value)} value={value || ''}>
                <option value="">{placeholder}</option>
                { options.map(([v, t]) => <option key={v} value={v}>{t}</option>) }
            </select>
        </div>
    )
}

function Range ({ idPrefix, legend, from, to, options, onChange, keys }) {
    return (
        <fieldset className={styles.fieldset}>
            <legend>{legend}</legend>
            <div className={styles.range}>
                <select aria-label={`${legend} od`} className={styles.control} id={`${idPrefix}${keys[0]}`} onChange={e => onChange(keys[0], e.target.value)} value={from || ''}>
                    <option value="">od</option>
                    { options.map(([v, t]) => <option key={v} value={v}>{t}</option>) }
                </select>
                <select aria-label={`${legend} do`} className={styles.control} id={`${idPrefix}${keys[1]}`} onChange={e => onChange(keys[1], e.target.value)} value={to || ''}>
                    <option value="">do</option>
                    { options.map(([v, t]) => <option key={v} value={v}>{t}</option>) }
                </select>
            </div>
        </fieldset>
    )
}

// Pola filtrów (rząd od 1024 px albo arkusz na telefonie): marka i model z ofert firmy (pojazdy), cena, rocznik
// (pojazdy); na desktopie także sortowanie.
function Fields ({ idPrefix, nav, offers, withSort }) {
    const { params, go } = nav
    const set = (k, v) => go({ [k]: v, strona: null, ...(k === 'marka' ? { model: null } : {}) })
    const priceValue = (k) => { const v = params[k]; return v ? numericSlug(readPriceSlug(v)) : '' }
    const prices = PRICES.map(p => [numericSlug(p), formatZl(p)])
    const years = YEARS.map(y => [String(y), String(y)])
    return (
        <div className={mix([styles.grid, offers.vehicles ? styles.gridVehicles : styles.gridOther])}>
            { offers.vehicles && <>
                <Select id={`${idPrefix}marka`} label="Marka" onChange={v => set('marka', v)} options={offers.facets.makes.map(m => [m, m])} placeholder="Wszystkie" value={params.marka} />
                <Select disabled={!params.marka} id={`${idPrefix}model`} label="Model" onChange={v => set('model', v)} options={offers.facets.models.map(m => [m, m])} placeholder={params.marka ? 'Wszystkie' : 'Najpierw marka'} value={params.model} />
            </> }
            <Range from={priceValue('cena-od')} idPrefix={idPrefix} keys={['cena-od', 'cena-do']} legend="Cena" onChange={set} options={prices} to={priceValue('cena-do')} />
            { offers.vehicles && <Range from={params['rocznik-od']} idPrefix={idPrefix} keys={['rocznik-od', 'rocznik-do']} legend="Rok produkcji" onChange={set} options={years} to={params['rocznik-do']} /> }
            { withSort && <SortField id={`${idPrefix}sortuj`} nav={nav} sortuj={offers.sortuj} /> }
        </div>
    )
}

function SortField ({ id, nav, sortuj, inline }) {
    return (
        <div className={inline ? styles.sort : styles.field}>
            <label htmlFor={id}>Sortuj</label>
            <select className={styles.control} id={id} onChange={e => nav.go({ sortuj: e.target.value === 'najnowsze' ? null : e.target.value, strona: null })} value={sortuj}>
                { SORTS.map(([v, t]) => <option key={v} value={v}>{t}</option>) }
            </select>
        </div>
    )
}

function Sheet ({ nav, offers, onClose, open }) {
    const ref = useRef(null)
    useEffect(() => {
        const d = ref.current
        if (!d) return
        if (open && !d.open) { d.showModal(); d.querySelector('select')?.focus() }
        if (!open && d.open) d.close()
    }, [open])
    return (
        <dialog aria-labelledby="salonSheetH" className={styles.sheet} onClose={onClose} ref={ref}>
            <div className={styles.sheetHead}>
                <h2 id="salonSheetH">Filtry</h2>
                <button aria-label="Zamknij filtry" className={styles.iconButton} onClick={onClose} type="button"><SvgIcon name="close" /></button>
            </div>
            { open && <Fields idPrefix="s-" nav={nav} offers={offers} /> }
            <div className={styles.sheetFoot}>
                <button className={styles.btnLine} onClick={() => nav.go(Object.fromEntries(FILTERS.map(k => [k, null]).concat([['strona', null]])))} type="button">Wyczyść</button>
                <button className={offerStyles.btnPrimary} onClick={onClose} type="button">Pokaż wyniki ({offers.results})</button>
            </div>
        </dialog>
    )
}

function Pager ({ nav, page, pages, onPage }) {
    const items = []
    for (let p = 1; p <= pages; p++) {
        if (p === 1 || p === pages || Math.abs(p - page) <= 1) items.push(p)
        else if (items[items.length - 1] !== '…') items.push('…')
    }
    const link = (p, children, extra = {}) => (
        <a href={nav.href({ strona: p }).url} onClick={e => { e.preventDefault(); onPage(p) }} {...extra}>{children}</a>
    )
    return (
        <nav aria-label="Strony oferty" className={styles.pagination}>
            <ul className={styles.pager}>
                { page > 1 && <li>{link(page - 1, <><SvgIcon name="chev-l" size="sm" />Poprzednia</>, { className: styles.step, rel: 'prev' })}</li> }
                { items.map((p, i) => p === '…'
                    ? <li key={`g${i}`}><span aria-hidden="true" className={styles.gap}>…</span></li>
                    : <li className={Math.abs(p - page) > 1 ? styles.far : undefined} key={p}>{ p === page ? <span aria-current="page">{p}</span> : link(p, p, { 'aria-label': `Strona ${p}` }) }</li>) }
                { page < pages && <li>{link(page + 1, <>Następna<SvgIcon name="chev" size="sm" /></>, { className: styles.step, rel: 'next' })}</li> }
            </ul>
            <p className={styles.pagerInfo}>Strona {page} z {pages}</p>
        </nav>
    )
}

export default function SalonOffer ({ firm, offers, visitor, write }) {
    const nav = useOfferNavigation(firm.subdomain)
    const [sheet, setSheet] = useState(false)
    const heading = useRef(null)
    const live = useRef(null)
    const sheetButton = useRef(null)
    const pendingFocus = useRef(null)
    const { tabs, active, results, total, page, pages, ads } = offers
    const activeFilters = FILTERS.filter(k => nav.params[k])

    // serca na subdomenie: token zalogowanego z serwera (po kontrolerze obserwowania, który ustawia token z przeglądarki)
    useEffect(() => {
        if (!visitor?.token || hasToken()) return
        setToken(visitor.token)
        document.querySelectorAll('[data-watch]').forEach(b => register(b.dataset.watch))
    }, [visitor, offers])

    const mounted = useRef(false)
    useEffect(() => {
        // liczba wyników ogłaszana po zmianie (nie przy wejściu na stronę)
        if (mounted.current && live.current) live.current.textContent = `${results} ${pluralize(results, OFFERS)}`
        mounted.current = true
        const target = pendingFocus.current
        pendingFocus.current = null
        if (target === 'heading') { heading.current?.scrollIntoView({ block: 'start' }); heading.current?.focus({ preventScroll: true }) }
        else if (target === 'chips') (document.querySelector(`.${styles.chip}`) || heading.current)?.focus()
    }, [offers])

    const onPage = (p) => { pendingFocus.current = 'heading'; nav.go({ strona: p }) }
    const unset = (k) => { pendingFocus.current = 'chips'; nav.go({ [k]: null, strona: null, ...(k === 'marka' ? { model: null } : {}) }) }
    const resetAll = () => { pendingFocus.current = 'chips'; nav.go(Object.fromEntries(FILTERS.map(k => [k, null]).concat([['strona', null]]))) }

    if (!total) {
        return (
            <section aria-labelledby="ofertaH" className={styles.offer} id="oferta">
                <div className={styles.offerHead}><h2 id="ofertaH">Oferta firmy</h2></div>
                <div className={styles.state}>
                    <h3>Ta firma nie ma teraz ogłoszeń</h3>
                    <p>Zapytaj o dostępne samochody albo usługi — firma odpowie w wiadomościach.</p>
                    { write && <div className={styles.stateActs}>{write}</div> }
                </div>
            </section>
        )
    }

    return (
        <section aria-labelledby="ofertaH" className={styles.offer} id="oferta">
            <div className={styles.offerHead}>
                <h2 id="ofertaH" ref={heading} tabIndex={-1}>Oferta firmy</h2>
                <p className={styles.count}><strong>{results}</strong> {pluralize(results, OFFERS)}</p>
            </div>
            { tabs.length > 1 && (
                <div aria-label="Kategorie" className={styles.tabs} role="group">
                    { tabs.map(t => (
                        <button aria-pressed={t.key === active} className={styles.tab} key={t.key} onClick={() => nav.go({ zakladka: t.key === 'all' ? null : t.name, strona: null })} type="button">
                            {t.name} <span>{t.count}</span>
                        </button>
                    )) }
                </div>
            ) }
            <div className={styles.panel}><Fields idPrefix="d-" nav={nav} offers={offers} withSort /></div>
            <div className={styles.tools}>
                <button aria-haspopup="dialog" className={styles.btnLine} onClick={() => setSheet(true)} ref={sheetButton} type="button"><SvgIcon name="sliders" />Filtry{activeFilters.length ? ` (${activeFilters.length})` : ''}</button>
                <SortField id="m-sortuj" inline nav={nav} sortuj={offers.sortuj} />
            </div>
            <Sheet nav={nav} offers={offers} onClose={() => { setSheet(false); setTimeout(() => sheetButton.current?.focus(), 0) }} open={sheet} />
            { activeFilters.length > 0 && (
                <div className={styles.chips}>
                    { activeFilters.map(k => (
                        <button aria-label={`Usuń filtr: ${LABELS[k]} ${chipValue(k, nav.params[k])}`} className={styles.chip} key={k} onClick={() => unset(k)} type="button">
                            {LABELS[k]}: {chipValue(k, nav.params[k])}<SvgIcon name="close" size="sm" />
                        </button>
                    )) }
                    <button className={styles.reset} onClick={resetAll} type="button">Wyczyść filtry</button>
                </div>
            ) }
            <div aria-busy={nav.busy} className={styles.results}>
                { ads.length
                    ? <>
                        <div className={styles.cards}>
                            { ads.map((offer, i) => <ListingOfferCard host={HOST} isFresh={isOfferFresh(offer, offers.now)} key={offer._shortid} offer={offer} priority={i === 0} sizes={SIZES} />) }
                        </div>
                        { pages > 1 && <Pager nav={nav} onPage={onPage} page={page} pages={pages} /> }
                    </>
                    : (
                        <div className={styles.state}>
                            <h3>Brak ogłoszeń dla tych filtrów</h3>
                            <p>Firma ma {total} {pluralize(total, OFFERS)}. Zmień albo usuń filtry.</p>
                            <ul className={styles.stateActs}>
                                { activeFilters.map(k => <li key={k}><button className={styles.btnLine} onClick={() => unset(k)} type="button">Bez filtra „{LABELS[k]}”</button></li>) }
                                <li><button className={offerStyles.btnPrimary} onClick={resetAll} type="button">Pokaż całą ofertę</button></li>
                            </ul>
                        </div>
                    ) }
            </div>
            <GalleryController />
            <WatchController />
            <noscript dangerouslySetInnerHTML={{ __html: `<style>.${cardStyles.gal},.${cardStyles.galDots},.${cardStyles.galBtn}{display:none}</style>` }} />
            <p aria-live="polite" className={styles.srOnly} ref={live} role="status" />
        </section>
    )
}
